import { useEffect, useMemo, useState } from "react";
import { useStores } from "../../stores/root-store";
import ProductItem from "./product-item";
import { observer } from "mobx-react";
import _, { filter, includes } from "lodash";
import { useAuth } from "../../contexts/auth-context";
import { useLocation, useSearchParams } from "react-router-dom";
import { useShopContext } from "../../contexts/shop-context";
import ShopClosedNotice from "../../shared/shop-closed-notice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import {
  creatHubConnection,
  getDisplayDeliveryType,
} from "../../utils/functions";
import OffersBanner from "./offers-banner";
import { HubConnection } from "@microsoft/signalr";
import { ApiConfig } from "../../services/api-config";
import { IProductOffer } from "../../stores/productOffer";

const ProductListing = observer(() => {
  const { pathname } = useLocation();
  const { isAuthenticated, userId, isShopAdmin, isShopUser } =
    useAuth();
  const { isShopClosed, shopTableCount } = useShopContext();
  const [hubConnection, setHubConnection] = useState<HubConnection>();
  const { productStore, cartStore, productOfferStore } = useStores();
  const {
    selectedCategoryId,
    setSelectedCategoryId,
    products,
    categories,
    fetchProducts,
    fetchProductCategories,
  } = productStore;
  const { getOrderTypeDetails, setTableNumber } = cartStore;
  const { receiveNewOffers } = productOfferStore;

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") ?? ""
  );
  const requestedSearchTerm = searchParams.get("search") ?? "";
  const orderTypeDetails = getOrderTypeDetails();
  const isTableOrderRoute = pathname.includes("/tbl-orders");
  const isManageProductsRequest =
    (isShopAdmin || isShopUser) && !isTableOrderRoute;
  const markShopClosed =
    isShopClosed && !isTableOrderRoute && !isShopAdmin && !isShopUser;

  useEffect(() => {
    // Navbar searches can update the query while this listing stays mounted.
    // Mirror the URL so table-order results update without leaving the order.
    setSearchTerm(requestedSearchTerm);
  }, [requestedSearchTerm]);

  useEffect(() => {
    if (!isTableOrderRoute) return;

    const requestedTable = Number(searchParams.get("table"));
    const maxTable = shopTableCount || 50;
    if (
      Number.isInteger(requestedTable) &&
      requestedTable > 0 &&
      requestedTable <= maxTable
    )
      setTableNumber(requestedTable);
  }, [isTableOrderRoute, searchParams, setTableNumber, shopTableCount]);

  useEffect(() => {
    fetchProducts();
  }, [isAuthenticated, userId, isShopClosed]);

  useEffect(() => {
    fetchProductCategories();
  }, []);

  const depts = useMemo(() => {
    return categories;
  }, [categories]);

  const prodGroups = useMemo(() => {
    const prods = searchTerm
      ? filter(
          products,
          (o) =>
            includes(o.productName.toUpperCase(), searchTerm.toUpperCase()) ||
            (o.category &&
              includes(o.category.name.toUpperCase(), searchTerm.toUpperCase()))
        )
      : products;
    return Object.entries(_.groupBy(prods, "category.name"));
  }, [products, selectedCategoryId, searchTerm]);

  //Singal R
  const orderHubCallback = (offer?: IProductOffer) => {
    if (offer) {
      receiveNewOffers(offer);
    }
  };

  useEffect(() => {
    const connection = creatHubConnection(
      ApiConfig.REST_URIS.productOffer.offerHub,
      "ReceiveOffer",
      orderHubCallback
    );
    setHubConnection(connection);

    return () => {
      hubConnection?.stop();
    };
  }, []);
  //

  return (
    <>
      <main
        className={isTableOrderRoute
          ? "table-order-page"
          : `menu-page${markShopClosed ? " menu-page--closed" : ""}`}
      >
      {!isManageProductsRequest && !isTableOrderRoute && !markShopClosed && (
        <OffersBanner />
      )}

      <div className="sticky-top menu-browse-toolbar">
        {markShopClosed && <ShopClosedNotice variant="menu" />}
        <nav
          className="nav nav-pills nav-justified horizontal-scrollable menu-categories"
          aria-label="Menu categories"
        >
          {depts?.map((item, index) => (
            <a
              key={item.name + index}
              className={`nav-link${selectedCategoryId === item.id ? " active" : ""}`}
              aria-current={selectedCategoryId === item.id ? "true" : undefined}
              href={`#${item.name}`}
              onClick={() => {
                if (searchParams.has("search")) {
                  searchParams.delete("search");
                  setSearchParams(searchParams);
                  setSearchTerm("");
                }
                setSelectedCategoryId(item.id);
              }}
            >
              {item.name}
            </a>
          ))}
        </nav>
      </div>
      {markShopClosed && <OffersBanner />}
      <div className="me-2 ms-2">
        {searchTerm && (
          <div className="text-default ps-lg-5 pe-lg-5">
            <span className="fs-6">
              {isTableOrderRoute ? "Menu items matching" : "Search result for"}{" "}
              "{searchTerm}"
            </span>
          </div>
        )}

        {!isTableOrderRoute && !markShopClosed && orderTypeDetails && (
          <div className="text-center text-default small mt-1">
            Ordering for{" "}
            {
              getDisplayDeliveryType(
                _.toNumber(orderTypeDetails.deliveryMethod)
              ).text
            }{" "}
            {orderTypeDetails.time === "0" ? "A.S.A.P" : orderTypeDetails.time}{" "}
            <a className="btn btn-default pt-0 pb-0 small" href="/">
              <FontAwesomeIcon icon={faEdit} /> Change
            </a>
          </div>
        )}

        <div className={isTableOrderRoute
          ? "table-order-grid mx-auto mt-3"
          : "menu-product-grid col-lg-9 mx-auto mt-3"}
        >
          {prodGroups?.length ? (
            prodGroups?.map(([cat, prods], i) => (
              <section key={cat + i} id={cat}>
                <div className="alert text-white bg-default rounded-0 mb-2 mt-2 pt-2 pb-2 h6">
                  {cat}
                </div>
                <div className="row">
                  {prods.map((prod, i) => (
                    <div
                      key={prod.productId + "_" + i}
                      className={
                        isTableOrderRoute
                          ? "col-12 col-sm-6 col-md-4 col-xl-3 pb-2"
                          : "col-6 col-lg-4 pb-1"
                      }
                    >
                      <ProductItem
                        product={prod}
                        isShopClosed={markShopClosed}
                        isManageProductsRequest={isManageProductsRequest}
                        isTableOrder={isTableOrderRoute}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))
          ) : (
            <div className="p-5 h4 text-center">No products available</div>
          )}
        </div>
      </div>
      </main>
    </>
  );
});

export default ProductListing;
